import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ScheduleStatus, Schedule } from '../types/schedule';
import { format } from 'date-fns';
import { getMySchedules, getAllSchedules, createScheduleRequest, deleteScheduleRequest } from '../utils/scheduleApi';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface DayOffRequest {
  id: string;
  date: Date;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
}

interface ScheduleContextType {
  // 스케줄 데이터
  scheduleData: Map<string, ScheduleStatus>;
  setScheduleData: (data: Map<string, ScheduleStatus>) => void;
  updateSchedule: (date: Date, status: ScheduleStatus) => void;
  
  // 휴무 신청
  dayOffRequests: DayOffRequest[];
  addDayOffRequest: (date: Date, reason: string) => Promise<void>;
  loadSchedules: () => Promise<void>;
  isLoading: boolean;
  
  // 유틸리티
  getScheduleStatus: (date: Date) => ScheduleStatus | undefined;
  isDayOffRequested: (date: Date) => boolean;
}

const ScheduleContext = createContext<ScheduleContextType | undefined>(undefined);

export const ScheduleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [scheduleData, setScheduleDataState] = useState<Map<string, ScheduleStatus>>(new Map());
  const [dayOffRequests, setDayOffRequests] = useState<DayOffRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 백엔드에서 스케줄 불러오기 (관리자면 전체, 일반 유저면 내 것만)
  const loadSchedules = async () => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    try {
      // 관리자면 전체 조회, 아니면 내 것만
      const response = user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN' 
        ? await getAllSchedules() 
        : await getMySchedules();
      
      console.log('📅 스케줄 로드:', response);
      
      if (response.success && response.data?.schedules) {
        const schedules: Schedule[] = response.data.schedules;
        
        console.log('✅ 로드된 스케줄:', schedules.length, '개');
        
        // Map으로 변환
        const newScheduleData = new Map<string, ScheduleStatus>();
        const newRequests: DayOffRequest[] = [];
        
        schedules.forEach(schedule => {
          const date = new Date(schedule.date);
          const dateKey = format(date, 'yyyy-MM-dd');
          
          // 상태 매핑
          let status: ScheduleStatus = 'PENDING';
          if (schedule.status === 'APPROVED') status = 'APPROVED';
          else if (schedule.status === 'REJECTED') status = 'REJECTED';
          else status = 'PENDING';
          
          newScheduleData.set(dateKey, status);
          
          // 요청 목록에 추가
          newRequests.push({
            id: schedule.id,
            date,
            reason: schedule.reason,
            status: schedule.status.toLowerCase() as 'pending' | 'approved' | 'rejected',
            createdAt: new Date(schedule.createdAt),
          });
        });
        
        console.log('📋 변환된 요청:', newRequests.length, '개');
        
        setScheduleDataState(newScheduleData);
        setDayOffRequests(newRequests);
      }
    } catch (error) {
      console.error('Failed to load schedules:', error);
      toast.error('스케줄을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 로그인 시 자동 로드
  useEffect(() => {
    if (isAuthenticated && user) {
      loadSchedules();
    }
  }, [isAuthenticated, user?.role]);

  const setScheduleData = (data: Map<string, ScheduleStatus>) => {
    setScheduleDataState(data);
  };

  const updateSchedule = (date: Date, status: ScheduleStatus) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    setScheduleDataState(prev => {
      const newData = new Map(prev);
      newData.set(dateKey, status);
      return newData;
    });
  };

  const addDayOffRequest = async (date: Date, reason: string) => {
    if (!isAuthenticated) {
      toast.error('로그인이 필요합니다.');
      return;
    }
    
    try {
      const response = await createScheduleRequest(date, reason);
      
      if (response.success) {
        toast.success('휴무 신청이 완료되었습니다! 🎉');
        
        // 다시 로드
        await loadSchedules();
      }
    } catch (error: any) {
      const message = error.response?.data?.message || '휴무 신청에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  const getScheduleStatus = (date: Date): ScheduleStatus | undefined => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return scheduleData.get(dateKey);
  };

  const isDayOffRequested = (date: Date): boolean => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const status = scheduleData.get(dateKey);
    return status === 'PENDING';
  };

  const value: ScheduleContextType = {
    scheduleData,
    setScheduleData,
    updateSchedule,
    dayOffRequests,
    addDayOffRequest,
    loadSchedules,
    isLoading,
    getScheduleStatus,
    isDayOffRequested,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};

export const useSchedule = (): ScheduleContextType => {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error('useSchedule must be used within ScheduleProvider');
  }
  return context;
};
